import { Fragment, useState } from "react";
import type { ReactNode } from "react";
import { WatchBlock } from "../../components/WatchBlock.tsx";
import { helpers } from "../../util/helpers.ts";
import { toWorker } from "../../util/toWorker.ts";
import type { GameAttributesLeague, Phase, Player, View } from "../../../../../common/types.ts";
import { PHASE, PLAYER } from "../../../../../common/constants.ts";
import clsx from "clsx";
import AwardsSummary from "./AwardsSummary.tsx";
import RatingsOverview from "./RatingsOverview.tsx";
import { CountryFlag } from "../../components/CountryFlag.tsx";
import { InjuryIcon } from "../../components/InjuryIcon.tsx";
import { SkillsBlock } from "../../components/SkillsBlock.tsx";
import { Mood } from "../../components/Mood.tsx";
import { PlayerPicture } from "../../components/PlayerPicture.tsx";
import { JerseyNumber } from "../../components/JerseyNumber.tsx";
import { Height } from "../../components/Height.tsx";
import { Weight } from "../../components/Weight.tsx";
import { getCols } from "../../util/getCols.ts";
import { NegotiationModal, useNegotiaionModal } from "../../components/NegotiationModal.tsx";
import i18n from "../../util/i18n.ts";
import { TeamAbbrevText } from "../../components/TeamAbbrevLink.tsx";
import { MobileTooltip } from "../../components/MobileTooltip.tsx";
import { formatPlayerNameFromLegacyName } from "../../util/playerNames.ts";
import { PositionText } from "../../util/positionNames.tsx";
import { translateTeamFullName } from "../../util/teamNames.ts";
import { MobileStatusMetric } from "../../components/MobileStatusMetric.tsx";
import { translateInjuryName } from "../../util/injuryNames.ts";
import {
  getPlayerAvailabilityStatus,
  type PlayerAvailabilityState,
} from "./playerStatusMetrics.ts";

const translateLocationPart = (part: string) =>
  i18n.t(`location.${part}`, { ns: "ui", defaultValue: part });

const translateLocation = (loc: string) => {
  const separator = i18n.language.startsWith("zh") ? "，" : ", ";
  return loc.split(", ").map(translateLocationPart).join(separator);
};

const translateCollege = (college: string) =>
  i18n.t(`college.${college}`, { ns: "ui", defaultValue: college });

const translateRelativeType = (type: string) =>
  i18n.t(`relativeType.${type}`, { ns: "ui", defaultValue: type });

const getAvailabilityText = (state: PlayerAvailabilityState) => {
  if (state === "healthy") {
    return i18n.t("roster.playerQuickHealthy", {
      ns: "ui",
      defaultValue: "Healthy",
    });
  }

  const key =
    state === "availableLimited"
      ? "statusAvailableLimited"
      : state === "shortTerm"
        ? "statusShortTerm"
        : state === "longTerm"
          ? "statusLongTerm"
          : "statusOut";

  return i18n.t(`injuries.${key}`, { ns: "ui" });
};

// R5-2: the worker sets `teamName` to a status word ("Free Agent" / "Draft
// Prospect" / "Retired") for players who are not on a roster. Those pass through
// translateTeamFullName unchanged (no teamRegion/teamName entry), so they leaked
// English in the zh bio line. Map the three known status strings to i18n keys;
// real team names return undefined and keep flowing through translateTeamFullName.
const translatePlayerStatusLabel = (teamName: string): string | undefined => {
  if (teamName === "Free Agent") {
    return i18n.t("playerProfile.statusFreeAgent", {
      ns: "ui",
      defaultValue: "Free Agent",
    });
  }
  if (teamName === "Draft Prospect") {
    return i18n.t("playerProfile.statusDraftProspect", {
      ns: "ui",
      defaultValue: "Draft Prospect",
    });
  }
  if (teamName === "Retired") {
    return i18n.t("playerProfile.statusRetired", {
      ns: "ui",
      defaultValue: "Retired",
    });
  }
  return undefined;
};

const Relatives = ({
  gender,
  getPlayerHref,
  pid,
  relatives,
}: {
  gender: GameAttributesLeague["gender"];
  getPlayerHref?: (pid: number) => string;
  pid: number;
  relatives: Player["relatives"];
}) => {
  const [showAll, setShowAll] = useState(false);

  if (relatives.length === 0) {
    return null;
  }

  const numToShow = showAll || relatives.length <= 3 ? relatives.length : 2;
  const numToHide = relatives.length - numToShow;

  return (
    <span
      data-player-relatives-owner-pid={pid}
      data-player-relatives-render-site="overview"
      data-player-relatives-state={numToHide === 0 ? "complete" : "collapsed"}
      data-player-relatives-total-count={relatives.length}
      data-player-relatives-visible-count={numToShow}
    >
      {relatives.slice(0, numToShow).map((rel) => {
        return (
          <Fragment key={rel.pid}>
            {translateRelativeType(helpers.getRelativeType(gender, rel.type))}:{" "}
            <a
              data-player-relative-pid={rel.pid}
              href={getPlayerHref?.(rel.pid) ?? helpers.leagueUrl(["player", rel.pid])}
            >
              {formatPlayerNameFromLegacyName(rel.name)}
            </a>
            <br />
          </Fragment>
        );
      })}
      {numToHide > 0 ? (
        <>
          <button
            className="btn btn-link p-0 m-0 border-0"
            data-player-relatives-hidden-count={numToHide}
            data-player-relatives-toggle={pid}
            onClick={() => {
              setShowAll(true);
            }}
          >
            {i18n.t("playerProfile.showMoreRelatives", {
              ns: "ui",
              defaultValue: "...show {{count}} more relatives",
              count: numToHide,
            })}
          </button>
          <br />
        </>
      ) : null}
    </span>
  );
};

const StatsSummary = ({
  name,
  onlyShowIf,
  p,
  phase,
  position,
  currentSeason,
  season,
  stats,
}: {
  name: string;
  onlyShowIf?: string[];
  p: View<"player">["player"];
  phase: Phase;
  position: string;
  currentSeason: number;
  season?: number;
  stats: string[];
}) => {
  if (onlyShowIf !== undefined) {
    if (!onlyShowIf.includes(position)) {
      return null;
    }
  }

  let ps: (typeof p)["stats"][number] | undefined;
  if (season !== undefined) {
    // Specific season was requested
    const playerStats = p.stats.filter((ps) => !ps.playoffs && ps.season === season);
    ps = playerStats.at(-1);
  }

  if (!ps) {
    if (p.tid === PLAYER.RETIRED) {
      // Find best season for retired player
      let maxValue = -Infinity;
      for (const row of p.stats) {
        if (row.playoffs) {
          continue;
        }

        const value = row.ws ?? -Infinity;
        if (value > maxValue) {
          ps = row;
          maxValue = value;
        }
      }
    } else {
      // Find current season for active player
      const playerStats = p.stats.filter(
        (ps) =>
          !ps.playoffs &&
          (ps.season === currentSeason ||
            (ps.season === currentSeason - 1 && phase === PHASE.PRESEASON)),
      );
      ps = playerStats.at(-1);
    }
  }

  const cols = getCols(["Summary", ...stats.map((stat) => `stat:${stat}`)]);

  if (name === "Shot Locations") {
    cols.at(-3)!.title = "M";
    cols.at(-2)!.title = "A";
    cols.at(-1)!.title = "%";
  }

  const separatorAfter = [0, 5, 9];

  const showPeakSeason = p.tid === PLAYER.RETIRED && season === undefined;

  // D5 (B3 leftover b): on mobile the wide summary table scrolled sideways
  // inside its own overflow container. Render a vertical two-column grid of
  // stat cells instead — season value primary, career value secondary — and
  // keep the desktop table unchanged.
  if (window.mobile) {
    const careerLabel = i18n.t("playerProfile.career", {
      ns: "ui",
      defaultValue: "Career",
    });
    const seasonLabel = ps
      ? showPeakSeason
        ? i18n.t("playerProfile.peakSeason", {
            ns: "ui",
            defaultValue: "Peak",
          })
        : String(ps.season)
      : undefined;

    return (
      <div className="mt-3" data-d5-player-summary-mobile="">
        <div className="d-flex justify-content-between small text-body-secondary mb-2">
          <span>{cols[0]!.title}</span>
          <span data-d5-player-summary-legend="">
            {seasonLabel !== undefined ? `${seasonLabel} / ${careerLabel}` : careerLabel}
          </span>
        </div>
        <dl className="datatable-mobile-card-fields mb-0">
          {stats.map((stat, i) => {
            const col = cols[i + 1]!;
            return (
              <div className="datatable-mobile-card-field" key={stat}>
                <dt>
                  <MobileTooltip title={col.desc}>
                    <span data-mobile-tooltip-host="player-stat-summary">{col.title}</span>
                  </MobileTooltip>
                </dt>
                <dd>
                  {ps ? (
                    <>
                      <span className="fw-semibold">
                        {helpers.roundStat((ps as any)[stat], stat)}
                      </span>{" "}
                      <span className="text-body-secondary">
                        / {helpers.roundStat(p.careerStats[stat]!, stat)}
                      </span>
                    </>
                  ) : (
                    <span className="fw-semibold">
                      {helpers.roundStat(p.careerStats[stat]!, stat)}
                    </span>
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      </div>
    );
  }

  return (
    <div className="player-stats-summary small-scrollbar">
      <table className="table table-sm table-borderless border-top-0 table-nonfluid text-center mt-3 mb-0">
        <thead>
          <tr>
            {cols.map((col, i) => {
              return (
                <th
                  key={i}
                  className={clsx({
                    "table-separator-right": separatorAfter.includes(i),
                    "table-separator-left": separatorAfter.includes(i - 1),
                    "text-start": i === 0,
                  })}
                >
                  <MobileTooltip title={col.desc}>
                    <span data-mobile-tooltip-host="player-stat-summary">{col.title}</span>
                  </MobileTooltip>
                </th>
              );
            })}
          </tr>
        </thead>
        {ps ? (
          <tbody>
            <tr>
              <th className="table-separator-right text-start">
                <MobileTooltip title={showPeakSeason ? String(ps.season) : undefined}>
                  <span>
                    {showPeakSeason
                      ? i18n.t("playerProfile.peakSeason", { ns: "ui", defaultValue: "Peak" })
                      : ps.season}
                  </span>
                </MobileTooltip>
              </th>
              {stats.map((stat, i) => {
                return (
                  <td
                    key={stat}
                    className={clsx({
                      "table-separator-right": separatorAfter.includes(i + 1),
                      "table-separator-left": separatorAfter.includes(i),
                    })}
                  >
                    {helpers.roundStat((ps as any)[stat], stat)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        ) : null}

        <tfoot>
          <tr>
            <th className="table-separator-right text-start">
              {i18n.t("playerProfile.career", { ns: "ui", defaultValue: "Career" })}
            </th>
            {stats.map((stat, i) => {
              return (
                <td
                  key={stat}
                  className={clsx({
                    "table-separator-right": separatorAfter.includes(i + 1),
                    "table-separator-left": separatorAfter.includes(i),
                  })}
                >
                  {helpers.roundStat(p.careerStats[stat]!, stat)}
                </td>
              );
            })}
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const TopStuff = ({
  bestPos,
  currentSeason,
  freeAgent,
  gender,
  getPlayerHref,
  injured,
  jerseyNumberInfos,
  phase,
  player,
  retired,
  season,
  showContract,
  showRatings,
  showTradeFor,
  spectator,
  statSummary,
  teamColors,
  teamJersey,
  teamName,
  teamURL,
  userTid,
  willingToSign,
}: Pick<
  View<"player">,
  | "bestPos"
  | "currentSeason"
  | "freeAgent"
  | "gender"
  | "injured"
  | "jerseyNumberInfos"
  | "phase"
  | "player"
  | "retired"
  | "showContract"
  | "showRatings"
  | "showTradeFor"
  | "spectator"
  | "statSummary"
  | "teamColors"
  | "teamJersey"
  | "teamName"
  | "teamURL"
  | "userTid"
  | "willingToSign"
> & {
  getPlayerHref?: (pid: number) => string;
  season?: number;
}) => {
  const wordSpace = i18n.language.startsWith("zh") ? "" : " ";

  let draftInfo: ReactNode = null;
  if (player.draft.round > 0) {
    draftInfo = (
      <>
        {i18n.t("playerProfile.draft", { ns: "ui", defaultValue: "Draft" })}:{" "}
        <a href={helpers.leagueUrl(["draft_history", player.draft.year])}>{player.draft.year}</a> -{" "}
        {i18n.t("playerProfile.roundPick", {
          ns: "ui",
          defaultValue: "Round {{round}} (Pick {{pick}})",
          round: player.draft.round,
          pick: player.draft.pick,
        })}
        {wordSpace}
        {i18n.t("playerProfile.by", { ns: "ui", defaultValue: "by" })}
        {wordSpace}
        <a href={helpers.leagueUrl(["roster", `${player.draft.abbrev}_${player.draft.tid}`])}>
          <TeamAbbrevText tid={player.draft.tid} abbrev={player.draft.abbrev} />
        </a>
        <br />
      </>
    );
  } else {
    draftInfo = (
      <>
        {i18n.t("playerProfile.undrafted", {
          ns: "ui",
          defaultValue: "Undrafted",
        })}
        : <a href={helpers.leagueUrl(["draft_history", player.draft.year])}>{player.draft.year}</a>
        <br />
      </>
    );
  }

  let contractInfo: ReactNode = null;
  if (season !== undefined || showContract) {
    let amount;
    let exp;
    if (season === undefined || season === currentSeason) {
      amount = player.contract.amount;
      exp = player.contract.exp;
    } else {
      amount = player.salaries.findLast((row) => row.season === season)?.amount;
    }

    contractInfo = (
      <>
        {freeAgent
          ? i18n.t("playerProfile.askingFor", { ns: "ui", defaultValue: "Asking for" })
          : i18n.t("playerProfile.contract", { ns: "ui", defaultValue: "Contract" })}
        : {amount !== undefined ? helpers.formatCurrency(amount, "M") : "???"}
        {i18n.t("playerProfile.perYear", { ns: "ui", defaultValue: "/yr" })}
        {exp !== undefined
          ? ` ${i18n.t("playerProfile.thru", { ns: "ui", defaultValue: "thru" })} ${exp}`
          : null}
        <br />
      </>
    );
  }

  let statusInfo: ReactNode = null;
  if (retired && season === undefined) {
    statusInfo = (
      <div className="d-flex align-items-center">
        <WatchBlock className="ms-0" pid={player.pid} defaultWatch={player.watch} />
      </div>
    );
  } else {
    let skills;
    if (season !== undefined) {
      skills = player.ratings.find((row) => row.season === season)?.skills;
    }
    if (!skills) {
      skills = player.ratings.at(-1)!.skills;
    }

    statusInfo = (
      <div className="d-flex align-items-center">
        {injured && !window.mobile ? <InjuryIcon className="ms-0" injury={player.injury} /> : null}
        <SkillsBlock
          className={injured && !window.mobile ? undefined : "skills-alone"}
          host="player-profile"
          skills={skills}
        />
        <WatchBlock className="ms-2" pid={player.pid} defaultWatch={player.watch} />
        {player.tid === PLAYER.FREE_AGENT ||
        player.tid === PLAYER.UNDRAFTED ||
        player.tid >= PLAYER.FREE_AGENT ? (
          <Mood
            className="ms-2"
            defaultType={
              player.tid === PLAYER.FREE_AGENT || player.tid === PLAYER.UNDRAFTED
                ? "user"
                : "current"
            }
            host="player-profile"
            p={player}
          />
        ) : null}
      </div>
    );
  }

  const height = <Height inches={player.hgt} />;

  const weight = <Weight pounds={player.weight} />;

  const college =
    player.college && player.college !== ""
      ? translateCollege(player.college)
      : i18n.t("playerProfile.noneCollege", { ns: "ui", defaultValue: "None" });

  const negotiationModal = useNegotiaionModal();
  const playerName = formatPlayerNameFromLegacyName(player.name);
  // M4-2: the player-centric trade entry. "I want him" is the primary CTA when
  // the player is on another team and tradable; it seeds the trade workbench
  // with this player as the target (via the existing `tradeFor` action, which
  // creates the trade record and navigates to /trade). Phase gating (trade
  // deadline / draft phases) stays on the /trade page's closed state, matching
  // the existing flow; `player.untradable` disables the button with its reason.
  const tradeForTeamName = translateTeamFullName(teamName);
  const tradeForText = i18n.t("playerProfile.tradeForWorkbench", {
    ns: "ui",
    defaultValue: "I want him — talk to {{team}}",
    team: tradeForTeamName,
  });
  const negotiateContractText = i18n.t("playerProfile.negotiateContract", {
    ns: "ui",
    defaultValue: "Negotiate contract",
  });

  const playerButtons = (
    <>
      {!spectator && showTradeFor ? (
        <button
          className="btn btn-primary"
          data-player-trade-for=""
          disabled={player.untradable}
          onClick={() => {
            toWorker("actions", "tradeFor", { pid: player.pid });
          }}
          aria-label={player.untradableMsg ?? tradeForText}
        >
          {tradeForText}
        </button>
      ) : null}
      {!spectator && freeAgent ? (
        <button
          className="btn btn-light-bordered"
          data-player-negotiate-contract=""
          disabled={!willingToSign}
          onClick={async () => {
            await negotiationModal.negotiate(player.pid);
          }}
          aria-label={
            willingToSign
              ? negotiateContractText
              : i18n.t("playerProfile.refusesToNegotiate", {
                  ns: "ui",
                  defaultValue: "{{name}} refuses to negotiate with you",
                  name: playerName,
                })
          }
        >
          {negotiateContractText}
        </button>
      ) : null}
    </>
  );

  const showRatingsOverview = (!retired || season !== undefined) && showRatings;
  const availability = getPlayerAvailabilityStatus(player.injury);
  const availabilityDetail =
    availability.state === "healthy"
      ? undefined
      : i18n.t("roster.playerQuickInjury", {
          ns: "ui",
          defaultValue: "{{type}} · {{count}} game(s)",
          type: translateInjuryName(player.injury.type),
          count: player.injury.gamesRemaining,
        });

  return (
    <div className="mb-3">
      <div className="d-sm-flex align-items-start">
        <div className="player-bio">
          <div className="d-flex">
            <div
              className="player-picture"
              style={{
                marginTop: player.imgURL ? 0 : -20,
              }}
            >
              <PlayerPicture
                face={player.face}
                imgURL={player.imgURL}
                colors={teamColors}
                jersey={teamJersey}
              />
            </div>
            <div>
              <strong>
                <PositionText pos={bestPos} display="full" />,{" "}
                {teamURL ? (
                  <a href={teamURL}>
                    {translatePlayerStatusLabel(teamName) ?? translateTeamFullName(teamName)}
                  </a>
                ) : (
                  (translatePlayerStatusLabel(teamName) ?? translateTeamFullName(teamName))
                )}
                {player.jerseyNumber ? <>, #{player.jerseyNumber}</> : null}
              </strong>
              <br />
              {height}, {weight}
              <br />
              {i18n.t("playerProfile.born", { ns: "ui", defaultValue: "Born" })}: {player.born.year}{" "}
              - {translateLocation(player.born.loc)}
              <CountryFlag className="ms-1" country={player.born.loc} />
              <br />
              {player.ageAtDeath === null || season !== undefined ? (
                <>
                  {i18n.t("playerProfile.age", { ns: "ui", defaultValue: "Age" })}: {player.age}
                  <br />
                </>
              ) : (
                <>
                  {i18n.t("playerProfile.died", {
                    ns: "ui",
                    defaultValue: "Died",
                  })}
                  : {player.diedYear} (
                  {i18n.t("playerProfile.ageYearsOld", {
                    ns: "ui",
                    defaultValue: "{{age}} years old",
                    age: player.ageAtDeath,
                  })}
                  )
                  <br />
                </>
              )}
              <Relatives
                gender={gender}
                getPlayerHref={getPlayerHref}
                pid={player.pid}
                relatives={player.relatives}
              />
              {draftInfo}
              {i18n.t("playerProfile.college", {
                ns: "ui",
                defaultValue: "College",
              })}
              : {college}
              <br />
              {i18n.t("playerProfile.experience", {
                ns: "ui",
                defaultValue: "Experience",
              })}
              :{" "}
              {player.experience === 0
                ? i18n.t("playerProfile.experienceNone", {
                    ns: "ui",
                    defaultValue: "none",
                  })
                : player.experience === 1
                  ? i18n.t("playerProfile.experienceOneYear", {
                      ns: "ui",
                      defaultValue: "1 year",
                    })
                  : i18n.t("playerProfile.experienceYears", {
                      ns: "ui",
                      defaultValue: "{{count}} years",
                      count: player.experience,
                    })}
              <br />
              {contractInfo}
              {statusInfo}
            </div>
          </div>

          {window.mobile && season === undefined && !retired ? (
            <div className="mt-3" data-player-availability-status={availability.state}>
              <MobileStatusMetric
                detail={availabilityDetail}
                label={i18n.t("injuries.statusDesc", {
                  ns: "ui",
                  defaultValue: "Availability status",
                })}
                score={availability.score}
                value={getAvailabilityText(availability.state)}
              />
            </div>
          ) : null}

          <div className="btn-group mt-2" data-player-action-rail="">
            {playerButtons}
          </div>
          {spectator && (showTradeFor || freeAgent) ? (
            <p
              className="alert alert-secondary py-2 small mt-2 mb-0"
              data-player-spectator-readonly=""
              role="status"
            >
              {i18n.t("playerProfile.spectatorReadonly", {
                ns: "ui",
                defaultValue:
                  "Spectator mode: the AI manages player transactions. You can review this player, but management actions are unavailable.",
              })}
            </p>
          ) : null}
          {player.careerStats.gp! > 0 ? (
            <>
              {statSummary.map(({ name, onlyShowIf, stats }) => (
                <StatsSummary
                  key={name}
                  name={name}
                  onlyShowIf={onlyShowIf}
                  position={player.ratings.at(-1)!.pos}
                  phase={phase}
                  currentSeason={currentSeason}
                  season={season}
                  stats={stats}
                  p={player}
                />
              ))}
            </>
          ) : null}
        </div>

        <div className="mt-3 mt-sm-0 text-nowrap overflow-auto small-scrollbar">
          {showRatingsOverview ? (
            <RatingsOverview ratings={player.ratings} season={season} />
          ) : null}
          {jerseyNumberInfos.length > 0 ? (
            <div
              className={clsx("d-flex flex-wrap", {
                "mt-2": showRatingsOverview || player.awards.length > 0,
              })}
              style={{
                gap: "0.5em",
              }}
            >
              {jerseyNumberInfos.map((info, i) => {
                let onClick;
                let extraText;
                const t = info.t;
                if (t && t.tid === userTid) {
                  const isCurrentTeamAndNumber = info.end >= currentSeason && t.tid === player.tid;

                  // Don't allow retiring current number, cause it behaves weirdly
                  if (!isCurrentTeamAndNumber) {
                    onClick = async () => {
                      if (info.retiredIndex >= 0) {
                        await toWorker("main", "retiredJerseyNumberDelete", {
                          tid: t.tid,
                          i: info.retiredIndex,
                        });
                      } else {
                        await toWorker("main", "retiredJerseyNumberUpsert", {
                          tid: t.tid,
                          info: {
                            number: info.number,
                            seasonRetired: currentSeason,
                            seasonTeamInfo: info.end,
                            pid: player.pid,
                            text: "",
                          },
                        });
                      }
                    };
                    extraText = i18n.t(
                      info.retiredIndex >= 0
                        ? "playerProfile.clickToUnretireJersey"
                        : "playerProfile.clickToRetireJersey",
                      {
                        ns: "ui",
                        defaultValue:
                          info.retiredIndex >= 0
                            ? "click to unretire jersey"
                            : "click to retire jersey",
                      },
                    );
                  }
                }
                return (
                  <JerseyNumber
                    key={i}
                    onClick={onClick}
                    extraText={extraText}
                    retired={info.retiredIndex >= 0}
                    {...info}
                  />
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      <AwardsSummary awards={player.awards} />

      <NegotiationModal {...negotiationModal.props} />
    </div>
  );
};

export default TopStuff;
